import { Checkbox } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';

import {
  DragAndDropOrderedTable,
  Row,
  TableColumn,
} from '@/components/DragAndDropOrderedTable';
import { Pane } from '@/components/Pane';
import { Button, Text } from '@/design-system';

import { RUNS_TABLE_COLUMNS } from '../utils/constants';

interface RunsTableConfigPaneProps {
  open: boolean;
  onClose: () => void;
  columns: Array<{
    id: string;
    label: string;
    visible: boolean;
    canHide: boolean;
  }>;
  initialPrioritizedColumns: string[];
  fixedColumnIds?: string[];
  onSave: (args: {
    prioritizedColumns: string[];
    columnVisibility: Record<string, boolean>;
  }) => void;
  onResetClick?: () => void;
}

export function RunsTableConfigPane(props: RunsTableConfigPaneProps) {
  const fixedColumnIds = useMemo(
    () =>
      props.fixedColumnIds ?? [
        RUNS_TABLE_COLUMNS.SELECT,
        RUNS_TABLE_COLUMNS.STATUS,
        RUNS_TABLE_COLUMNS.NAME,
      ],
    [props.fixedColumnIds]
  );

  const initialRows = useMemo<Row[]>(() => {
    const source = props.columns.filter(
      (c) => c.canHide && !fixedColumnIds.includes(c.id)
    );
    return source.map((col) => ({
      id: String(col.id),
      values: { name: col.label, visible: col.visible },
    }));
  }, [props.columns, fixedColumnIds]);

  const defaultVisibility = useMemo(() => {
    return Object.fromEntries(
      initialRows.map((r) => [r.id, Boolean(r.values.visible)])
    );
  }, [initialRows]);

  const [rows, setRows] = useState<Row[]>(initialRows);
  const [visibility, setVisibility] =
    useState<Record<string, boolean>>(defaultVisibility);

  useEffect(() => {
    setRows(initialRows);
    setVisibility(defaultVisibility);
  }, [props.open, initialRows, defaultVisibility]);

  const tableColumns: TableColumn[] = useMemo(
    () => [
      { key: 'name', header: 'Column', width: '70%' },
      {
        key: 'visible',
        header: 'Visible',
        width: '30%',
        renderCell: (item) => {
          const columnId = item.id;
          const isVisible = visibility[columnId] ?? true;
          return (
            <Checkbox
              size="sm"
              checked={isVisible}
              onChange={(e) => {
                const checked = e.target.checked;
                setVisibility((prev) => ({ ...prev, [columnId]: checked }));
              }}
            />
          );
        },
      },
    ],
    [visibility]
  );

  return (
    <Pane
      open={props.open}
      onClose={props.onClose}
      title="Configure Columns"
      className="flex h-[500px] flex-col p-0"
      dialogStyle={{
        marginLeft: 'calc(max(4.5rem, 100vw - max(20vw, 900px)))',
      }}
      topBarRightElement={
        <div className="flex items-center gap-2 pr-4">
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => props.onClose()}
            size="md"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            size="md"
            onClick={() =>
              props.onSave({
                prioritizedColumns: rows.map((r) => r.id),
                columnVisibility: visibility,
              })
            }
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="h-full overflow-y-auto p-4">
        <Text className="mb-4" color="tertiary" variant="body">
          Drag to reorder columns and toggle their visibility. Fixed columns
          (Select, Status, Name) are not shown here. These are persisted for
          this browser not for the project.
        </Text>
        <DragAndDropOrderedTable
          title="Columns"
          rows={rows}
          tableColumns={tableColumns}
          hideAddButton
          onChange={(items) => {
            setRows(items);
          }}
          onReset={() => {
            setRows(initialRows);
            setVisibility(defaultVisibility);
            props.onResetClick?.();
          }}
        />
      </div>
    </Pane>
  );
}
